import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { resolveVerticalStack } from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { computeLabelButtonRow, frameButtonX } from "./tabRowLayout.js";
import { resolveOriginX } from "./directionalUi.js";
import spec from "../data/layouts/rebirth.json";

/** 환생 강화 버튼 너비 — wrapWidth 산정 + RebirthTabView widthOverride 공용 SSoT (뷰가 import). */
export const BUTTON_WIDTH = 92;
/** 텍스트가 버튼 좌측 경계를 침범하지 않게 두는 여백(px). */
const WRAP_MARGIN = 8;
const SCROLLBAR_GUTTER = 12;
/** 환생탭은 탭 라벨과 중복되는 제목 텍스트를 표시하지 않는다. */
const TITLE_H = 0;
/** 팁 캐러셀 영역 — 높이 40. 제목이 없으므로 콘텐츠 최상단에서 시작한다. */
const TIP_H = 40;
const TIP_GAP = 0;
export const CURRENCY_CHIP_W = 104;
export const CURRENCY_CHIP_H = 24;
export const CURRENCY_CHIP_ICON_SIZE = 16;
const CURRENCY_CHIP_PAD_X = 6;
/** 팁 캐러셀 우측 `>` 버튼 아래에 가깝게 보이도록 칩 내부 표시를 우측으로 민다. */
const CURRENCY_CHIP_CONTENT_SHIFT_X = 48;

/** 환생 포인트 칩 전용 짧은 정수 표기. 좁은 칩 안에서 긴 숫자가 넘치지 않게 한다. */
export function formatRebirthPointChipValue(value: number): string {
  const n = Math.max(0, Math.floor(value));
  if (n < 10_000) return String(n);
  if (n < 1_000_000) return `${Math.floor(n / 1_000)}K`;
  if (n < 1_000_000_000) return `${Math.floor(n / 1_000_000)}M`;
  return `${Math.floor(n / 1_000_000_000)}B`;
}

export interface RebirthRowLayout {
  /** 행 배경 프레임 (스크롤 로컬 좌표, origin 0.5) — 스탯 탭과 동일 양식. */
  readonly frame: { x: number; y: number; width: number; height: number };
  /** 행 좌측 소형 스탯 아이콘. */
  readonly icon: { x: number; y: number };
  /** 스크롤 로컬 좌표 (origin은 rowTextOriginX, 세로 중앙). */
  readonly name: { x: number; y: number };
  readonly breakdown: { x: number; y: number };
  readonly result: { x: number; y: number };
  readonly button: { x: number; y: number };
  /** name/breakdown/result 자동 줄바꿈 폭 — 버튼 좌측 경계까지(가로 겹침 방지). */
  readonly wrapWidth: number;
}

export interface RebirthLayout {
  /** 헤더(제목/정보) origin x. */
  readonly headTextOriginX: number;
  /** 스크롤 행 라벨/값 origin x. */
  readonly rowTextOriginX: number;
  /** 헤더 제목 영역은 중복 타이틀 제거로 높이 0이다. */
  readonly title: Rect;
  /** 제목 아래 팁 캐러셀 영역 (전체폭) — TipCarouselView 배치 기준(스탯 탭 동형). */
  readonly tip: Rect;
  /** 헤더 정보 영역 (2줄, origin headTextOriginX) — 높이 인지로 스크롤 영역과 세로 침범 차단. */
  readonly info: Rect;
  /** 배수 선택 영역 (스탯 탭과 동일한 x1/x10/x100/xMax 세그먼트). */
  readonly multiplier: Rect;
  /** 보유 환생 포인트 표시 칩 — HUD 재화 칩과 같은 9-slice/icon/text 구조. */
  readonly currencyChip: {
    readonly bg: Rect;
    readonly icon: { x: number; y: number };
    readonly label: { x: number; y: number; originX: number };
  };
  /** 하단 고정 환생 버튼 중심 (방향 무관 중앙). */
  readonly rebirthBtn: { x: number; y: number };
  /** 스크롤 영역 rect (info 아래 ~ 환생 버튼 위, 세로 스택 fill). */
  readonly scroll: Rect;
  /** 스크롤 내부 행 (로컬 좌표). */
  readonly rows: readonly RebirthRowLayout[];
  /** 스크롤 콘텐츠 총 높이. */
  readonly contentHeight: number;
}

/**
 * 환생 탭 좌표 — 헤더(tip/info)+스크롤(fill)+배수바(하단)를 resolveVerticalStack(@yjsgames/phaser-ui)로 위→아래
 * 누적 배치해 info(2줄)와 ScrollableList 영역의 **세로 침범을 구조적으로 차단**한다
 * (info 높이 = infoLines×infoLineHeight 인지 → scroll이 자동으로 그 아래). 다음 요소 y = 이전 y+height+gap이라
 * 겹침이 수학적으로 불가능하며, rectOverlap 단위테스트가 회귀를 정적으로 검증한다.
 * 하단 환생 버튼은 panelBottom 기준 고정, 스택은 그 위(btnY - listBottomGap)까지만 차지한다.
 * 배수 선택기는 스탯 탭과 같은 하단 고정 위치라 구매 탭 간 조작 위치가 흔들리지 않는다.
 * 행 내부(스크롤 cell)는 스탯 탭(statUpgradeLayout)과 동일 양식 — 프레임/좌측 아이콘/3줄 텍스트/우측 버튼,
 * rebirth.json offset + computeLabelButtonRow(RTL), wrapWidth로 버튼 가로 침범 차단.
 * @param panelBottom LAYOUT.tabPanelBottom — 하단 버튼/스택 높이 기준.
 */
export function computeRebirthLayout(
  content: Rect,
  dir: TextDirection,
  rowCount: number,
  panelBottom: number,
): RebirthLayout {
  const head = computeLabelButtonRow(content.x, content.width, 0, dir);
  const btnY = panelBottom - spec.rebirthBtnBottomInset;
  // 헤더 2종 + 스크롤(fill) + 하단 배수바를 세로 스택으로 누적 — 세로 겹침 구조적 차단.
  const stackBottom = btnY - spec.listBottomGap;
  const stack = resolveVerticalStack(
    {
      children: [
        { id: "title", height: TITLE_H },
        { id: "tip", height: TIP_H, gapTop: TIP_GAP },
        { id: "info", height: spec.infoLines * spec.infoLineHeight, gapTop: spec.titleGap },
        { id: "scroll", fill: true, gapTop: spec.scrollGapTop },
        { id: "multiplier", height: 26, gapTop: 8 },
      ],
    },
    { x: content.x, y: content.y, width: content.width, height: stackBottom - content.y },
  );
  const titleRect = stack.title!;
  const tipRect = stack.tip!;
  const infoRect = stack.info!;
  const multiplierRect = stack.multiplier!;
  const scrollRect = stack.scroll!;
  const currencyChipX =
    dir === "rtl" ? content.x : content.x + content.width - CURRENCY_CHIP_W;
  const currencyChipY = infoRect.y;
  const currencyLabelOriginX = dir === "rtl" ? 1 : 0;
  const currencyIconX =
    dir === "rtl"
      ? currencyChipX +
        CURRENCY_CHIP_W -
        CURRENCY_CHIP_PAD_X -
        CURRENCY_CHIP_ICON_SIZE / 2 -
        CURRENCY_CHIP_CONTENT_SHIFT_X
      : currencyChipX +
        CURRENCY_CHIP_PAD_X +
        CURRENCY_CHIP_ICON_SIZE / 2 +
        CURRENCY_CHIP_CONTENT_SHIFT_X;
  const currencyLabelX =
    dir === "rtl"
      ? currencyChipX +
        CURRENCY_CHIP_W -
        CURRENCY_CHIP_PAD_X -
        CURRENCY_CHIP_ICON_SIZE -
        4 -
        CURRENCY_CHIP_CONTENT_SHIFT_X
      : currencyChipX +
        CURRENCY_CHIP_PAD_X +
        CURRENCY_CHIP_ICON_SIZE +
        4 +
        CURRENCY_CHIP_CONTENT_SHIFT_X;

  // 버튼 x는 프레임 경계에서 파생(frameButtonX — 마진 포함) — 프레임 밖 침범이 구조적으로 불가능.
  const frameGeom = {
    x: (content.width - SCROLLBAR_GUTTER + 4) / 2,
    width: content.width - SCROLLBAR_GUTTER - 4,
  };
  const buttonX = frameButtonX(frameGeom, BUTTON_WIDTH, dir);
  const iconLx = dir === "rtl" ? content.width - spec.labelPad - 12 : spec.labelPad + 12;
  const labelLx = dir === "rtl" ? content.width - spec.labelPad - 30 : spec.labelPad + 30;
  const buttonEdge = dir === "rtl" ? buttonX + BUTTON_WIDTH / 2 : buttonX - BUTTON_WIDTH / 2;
  const wrapWidth =
    dir === "rtl"
      ? Math.max(48, labelLx - buttonEdge - WRAP_MARGIN)
      : Math.max(48, buttonEdge - labelLx - WRAP_MARGIN);

  const rows: RebirthRowLayout[] = [];
  for (let i = 0; i < rowCount; i++) {
    const cy = spec.rowHeight / 2 + i * spec.rowHeight;
    rows.push({
      frame: { x: frameGeom.x, y: cy, width: frameGeom.width, height: spec.rowHeight - 6 },
      icon: { x: iconLx, y: cy },
      name: { x: labelLx, y: cy + spec.nameDy },
      breakdown: { x: labelLx, y: cy + spec.breakdownDy },
      result: { x: labelLx, y: cy + spec.resultDy },
      button: { x: buttonX, y: cy },
      wrapWidth,
    });
  }

  return {
    headTextOriginX: head.textOriginX,
    rowTextOriginX: resolveOriginX(dir),
    // 텍스트 x는 head.textX(RTL 미러), y/height는 스택 결과(세로 침범 차단).
    title: { x: head.textX, y: titleRect.y, width: content.width, height: titleRect.height },
    tip: { x: content.x, y: tipRect.y, width: content.width, height: tipRect.height },
    info: { x: head.textX, y: infoRect.y, width: content.width, height: infoRect.height },
    multiplier: { x: content.x, y: multiplierRect.y, width: content.width, height: multiplierRect.height },
    currencyChip: {
      bg: {
        x: currencyChipX,
        y: currencyChipY,
        width: CURRENCY_CHIP_W,
        height: CURRENCY_CHIP_H,
      },
      icon: {
        x: currencyIconX,
        y: currencyChipY + CURRENCY_CHIP_H / 2,
      },
      label: {
        x: currencyLabelX,
        y: currencyChipY + CURRENCY_CHIP_H / 2,
        originX: currencyLabelOriginX,
      },
    },
    rebirthBtn: { x: content.x + content.width / 2, y: btnY },
    scroll: scrollRect,
    rows,
    contentHeight: rowCount * spec.rowHeight,
  };
}
